import { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Clock, ArrowLeft } from "lucide-react";
import { ShareButtons } from "../components/ShareButtons";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import axios from "axios";
import { API } from "../App";

const NAV_LINKS = [
  { label: "Home", path: "/" },
  { label: "Browse", path: "/browse" },
  { label: "Blog", path: "/blog" },
];

export default function BlogPostPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/blog/${slug}`),
      axios.get(`${API}/blog`)
    ]).then(([postRes, allRes]) => {
      setPost(postRes.data);
      const allOthers = (allRes.data || []).filter(p => p.slug !== slug);
      // Prioritize same-category posts for "Keep Reading"
      const sameCategory = allOthers.filter(p => p.category === postRes.data.category);
      const different = allOthers.filter(p => p.category !== postRes.data.category);
      const related = [...sameCategory, ...different].slice(0, 3);
      setRelatedPosts(related);
      setLoading(false);
    }).catch(() => {
      setLoading(false);
      navigate("/blog");
    });
  }, [slug, navigate]);

  useEffect(() => {
    if (post) {
      document.title = post.title + " | The Reading Room Blog";
    }
  }, [post]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-light)' }}>
        <div className="w-8 h-8 border-3 rounded-full animate-spin" style={{ borderColor: 'var(--highlight)', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  if (!post) return null;

  const canonicalUrl = "https://thereading-room.com/blog/" + post.slug;
  const ogImage = "https://thereading-room.com" + post.image;

  return (
    <>
      <Helmet>
        <meta name="description" content={post.excerpt} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={`${post.title} | The Reading Room Blog`} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:type" content="article" />
      </Helmet>

      {/* Nav */}
      <nav className="sticky top-0 z-50 border-b" style={{ background: 'var(--bg-light)', borderColor: 'var(--highlight-border-light)' }}>
        <div className="max-w-6xl mx-auto px-5 flex items-center justify-between h-14">
          <Link to="/" className="text-lg font-bold tracking-tight" style={{ fontFamily: "'Playfair Display', serif", color: 'var(--text)' }}>
            The Reading Room
          </Link>
          <div className="flex items-center gap-6">
            {NAV_LINKS.map(l => (
              <Link key={l.path} to={l.path} className="text-sm font-medium transition-colors hover:opacity-70" style={{ color: 'var(--muted2)' }}>
                {l.label}
              </Link>
            ))}
          </div>
        </div>
      </nav>

      <main style={{ background: 'var(--bg-light)', minHeight: '100vh' }}>
        <article className="max-w-3xl mx-auto px-5 py-12">
          {/* Back */}
          <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm mb-8 transition-colors hover:opacity-70" style={{ color: 'var(--muted)' }}>
            <ArrowLeft className="w-4 h-4" />
            Back to all articles
          </Link>

          {/* Header */}
          <div className="mb-8">
            <span
              className="inline-block px-3 py-1 rounded-full text-[10px] font-bold tracking-wide uppercase mb-4"
              style={{ background: post.category_bg, color: post.category_color }}
            >
              {post.category}
            </span>
            <h1 className="text-3xl md:text-4xl font-bold leading-tight mb-4" style={{ fontFamily: "'Playfair Display', serif", color: 'var(--text)' }} data-testid="blog-post-title">
              {post.title}
            </h1>
            <div className="flex items-center gap-4 text-sm" style={{ color: 'var(--muted)' }}>
              <span className="flex items-center gap-1.5">
                <Clock className="w-4 h-4" />
                {post.read_time}
              </span>
              <span>
                {new Date(post.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </span>
            </div>
          </div>

          {/* Hero Image */}
          <div className="rounded-xl overflow-hidden mb-10" style={{ border: '0.5px solid var(--highlight-border-light)' }}>
            <img
              src={post.image}
              alt={post.title}
              className="w-full h-auto"
              data-testid="blog-post-image"
            />
          </div>

          {/* Markdown Content */}
          <div className="prose-reading-room" data-testid="blog-post-content">
            <ReactMarkdown
              rehypePlugins={[rehypeRaw]}
              components={{
                h1: ({ children }) => <h1 className="text-2xl md:text-3xl font-bold mt-8 mb-4" style={{ fontFamily: "'Playfair Display', serif", color: 'var(--text)' }}>{children}</h1>,
                h2: ({ children }) => <h2 className="text-xl md:text-2xl font-bold mt-8 mb-3" style={{ fontFamily: "'Playfair Display', serif", color: 'var(--text)' }}>{children}</h2>,
                h3: ({ children }) => <h3 className="text-lg font-bold mt-6 mb-2" style={{ color: 'var(--text)' }}>{children}</h3>,
                p: ({ children }) => <p className="text-base leading-[1.8] mb-4" style={{ color: 'var(--text)' }}>{children}</p>,
                strong: ({ children }) => <strong className="font-bold" style={{ color: 'var(--text)' }}>{children}</strong>,
                em: ({ children }) => <em className="italic" style={{ color: 'var(--muted2)' }}>{children}</em>,
                blockquote: ({ children }) => (
                  <blockquote className="my-6 pl-5 py-2" style={{ borderLeft: '3px solid var(--accent)', background: 'var(--highlight-muted)' }}>
                    {children}
                  </blockquote>
                ),
                ul: ({ children }) => <ul className="list-disc pl-6 mb-4 space-y-1" style={{ color: 'var(--text)' }}>{children}</ul>,
                ol: ({ children }) => <ol className="list-decimal pl-6 mb-4 space-y-1" style={{ color: 'var(--text)' }}>{children}</ol>,
                li: ({ children }) => <li className="text-base leading-[1.7]">{children}</li>,
                hr: () => <hr className="my-8" style={{ borderColor: 'var(--highlight-border-light)' }} />,
                a: ({ href, children }) => <a href={href} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 transition-colors hover:opacity-70" style={{ color: 'var(--accent)' }}>{children}</a>,
                img: ({ src, alt }) => (
                  <img src={src} alt={alt || ''} className="rounded-lg my-6 mx-auto" style={{ maxWidth: '180px', height: 'auto', boxShadow: '0 4px 16px rgba(0,0,0,0.10)', border: '0.5px solid var(--highlight-border-light)' }} />
                ),
              }}
            >
              {post.content}
            </ReactMarkdown>
          </div>

          <ShareButtons
            title={post.title}
            excerpt={post.excerpt}
            url={`https://thereading-room.com/blog/${post.slug}`}
            testIdPrefix="blog-share"
          />

          {/* Back to blog */}
          <div className="mt-12 pt-8" style={{ borderTop: '1px solid var(--highlight-border-light)' }}>
            <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm font-semibold transition-colors hover:opacity-70" style={{ color: 'var(--accent)' }}>
              <ArrowLeft className="w-4 h-4" />
              Back to all articles
            </Link>
          </div>

          {/* Keep Reading */}
          {relatedPosts.length > 0 && (
            <section className="mt-14" data-testid="keep-reading-section">
              <h2 className="text-xl md:text-2xl font-bold mb-6" style={{ fontFamily: "'Playfair Display', serif", color: 'var(--text)' }}>
                Keep Reading
              </h2>
              <div className="grid gap-4 sm:grid-cols-3">
                {relatedPosts.map((rp) => (
                  <Link
                    key={rp.slug}
                    to={`/blog/${rp.slug}`}
                    className="group rounded-xl overflow-hidden transition-all duration-300 hover:shadow-md"
                    style={{ background: 'var(--card-bg)', border: '1px solid var(--highlight-border-light)' }}
                    data-testid={`keep-reading-card-${rp.slug}`}
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={rp.image}
                        alt={rp.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-4">
                      <span
                        className="inline-block px-2 py-0.5 rounded-full text-[9px] font-bold tracking-wide uppercase mb-2"
                        style={{ background: rp.category_bg || '#F0EBE1', color: rp.category_color || '#5D4A32' }}
                      >
                        {rp.category}
                      </span>
                      <h3 className="text-sm font-bold leading-snug mb-2 line-clamp-2 transition-colors group-hover:opacity-80" style={{ fontFamily: "'Playfair Display', serif", color: 'var(--text)' }}>
                        {rp.title}
                      </h3>
                      <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--muted)' }}>
                        <Clock className="w-3 h-3" />
                        {rp.read_time}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>

      {/* Footer */}
      <footer className="py-8 text-center text-xs border-t" style={{ background: 'var(--bg-light)', borderColor: 'var(--highlight-border-light)', color: 'var(--muted)' }}>
        <p>&copy; {new Date().getFullYear()} The Reading Room. All rights reserved.</p>
      </footer>
    </>
  );
}
